import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Flex } from "styles/utility-components";

import CompanyUserSelect from "components/form_components/CompanyUserSelect";
import { Input, TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import DateInput from "components/ui/input/DateInput";
import toast from "components/ui/toast/Toast";
import { useUpdateBidResponses } from "lib/hooks/api/bid/bid_responses";
import { useCreateBidResponse } from "lib/hooks/api/bid/bid_responses/useCreateBidResponse";
import { routes } from "lib/routes";
import { BidResponseStageDropdownSelect } from "../bid-response/bid_response_stage/BidResponseStageDropdownSelect";
import { BidResponseFormInput, BidResponseFormOutput, bidResponseSchema } from "./utils";

export type { BidResponseFormOutput } from "./utils";

type Props = {
  type: "create" | "edit";
  defaultValues?: Partial<BidResponseFormOutput>;
  onComplete: () => void;
  onCancel: () => void;
  navigateToNewBidResponse?: boolean;
};

export function BidResponseForm({
  type,
  defaultValues,
  onComplete,
  onCancel,
  navigateToNewBidResponse = true,
}: Props) {
  const navigate = useNavigate();
  const { formState, control, handleSubmit, reset } = useForm<
    BidResponseFormInput,
    unknown,
    BidResponseFormOutput
  >({
    defaultValues: defaultValues,
    resolver: zodResolver(bidResponseSchema),
  });

  const { mutate: createBidResponse, isPending: isLoading } = useCreateBidResponse({
    onSuccess: (data) => {
      reset();
      if (navigateToNewBidResponse) {
        const newId = data.createBidResponse?.id;
        const to = defaultValues?.bidId
          ? `/bids/${defaultValues.bidId}/bid_responses/${newId}`
          : `${routes.bidResponses}/${newId}`;
        void navigate({ to });
        return;
      }
      onComplete?.();
      toast.success("Bid response created successfully");
    },
    onError: () => toast.error("Failed to create bid response"),
  });

  const { mutate: updateBidResponse } = useUpdateBidResponses({
    onSuccess() {
      reset();
      onComplete?.();
      toast.success(`Bid question saved`);
    },
    onError() {
      toast.error(`Failed to update bid question`);
    },
  });

  const onSubmit = (data: BidResponseFormOutput) => {
    const { id, bidId, ...bidResponse } = data;
    if (type === "edit" && id) {
      updateBidResponse({
        input: {
          ids: [id],
          ...bidResponse,
        },
      });
    } else {
      createBidResponse({
        input: {
          ...bidResponse,
          assigneeId: bidResponse.assigneeId ?? undefined,
          reviewerId: bidResponse.reviewerId ?? undefined,
          // Creating with an explicitly null due date is meaningless, so send nothing.
          dueDate: bidResponse.dueDate ?? undefined,
          bidId: bidId,
        },
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Input
        name="title"
        control={control}
        label="Response title"
        required
        placeholder="Name this response"
      />

      <TextArea
        name="description"
        control={control}
        label="Question"
        placeholder="Describe what this response should cover"
        autoSize={{ minRows: 4 }}
      />

      <Input
        name="reference"
        control={control}
        label="Reference (optional)"
        placeholder="Enter the question number / reference"
      />

      <Input
        name="weighting"
        control={control}
        label="Weighting (optional)"
        placeholder="Provide the weighting of this question"
      />

      <Input
        name="formattingRequirements"
        control={control}
        label="Formatting requirements (optional)"
        placeholder="Provide the formatting requirements of this question"
      />

      <CompanyUserSelect
        name="assigneeId"
        control={control}
        defaultValue={null}
        label="Assignee (optional)"
        placeholder="Select assignee"
      />

      <CompanyUserSelect
        name="reviewerId"
        defaultValue={null}
        control={control}
        label="Reviewer (optional)"
        placeholder="Select reviewer"
      />

      <BidResponseStageDropdownSelect
        name="stageId"
        control={control}
        label="Status"
        placeholder="Select status"
        required
      />

      <Field>
        <FieldLabel>Due date (optional)</FieldLabel>
        <DateInput
          name="dueDate"
          control={control}
          calendarProps={{ mode: "single" }}
          triggerWidth="100%"
        />
      </Field>

      <Flex gap={8} flexGrow={1} justifyContent="flex-end">
        <Button buttonType="secondary" loading={isLoading} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          buttonType="primary"
          type="submit"
          disabled={!formState.isValid}
          loading={isLoading}
        >
          Save
        </Button>
      </Flex>
    </Form>
  );
}

const Form = styled.form({
  height: "100%",

  display: "flex",
  flexDirection: "column",
  gap: 24,
  textAlign: "left",
});

// DateInput renders no label of its own, unlike the other form components here.
const Field = styled.div({
  display: "flex",
  flexDirection: "column",
});

const FieldLabel = styled.label({
  display: "inline-block",
  marginBottom: 4,
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "22px",
});
